import { useEffect, useRef } from "react";
import { toast as rht } from "react-hot-toast";

// Path is served fresh (must be no-cache on the server) so we always see the
// build time of the *deployed* app, not the one baked into this bundle.
const VERSION_URL = "/version.json";
const POLL_INTERVAL_MS = 60_000;
const TOAST_ID = "app-update-available";

async function fetchLatestBuildTime(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { buildTime?: string };
    return data.buildTime ?? null;
  } catch {
    return null;
  }
}

function showUpdateToast() {
  rht(
    (t) => (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span>A new version is available.</span>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: "#4f6bdc",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "6px 12px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Refresh
        </button>
        <button
          onClick={() => rht.dismiss(t.id)}
          aria-label="Dismiss"
          style={{
            background: "transparent",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      </div>
    ),
    { id: TOAST_ID, duration: Infinity, position: "top-right" }
  );
}

/**
 * Polls /version.json and, when the deployed build time differs from the one
 * baked into the running bundle, prompts the user to reload. Renders nothing.
 *
 * NOTE: This is only effective if the server serves index.html (and
 * version.json) with a no-cache policy — otherwise a reload just re-fetches the
 * stale cached bundle and the prompt reappears.
 */
export default function VersionChecker() {
  const promptedRef = useRef(false);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    const currentBuildTime = __BUILD_TIME__;
    let cancelled = false;

    const check = async () => {
      if (promptedRef.current || document.hidden) return;
      const latest = await fetchLatestBuildTime();
      if (cancelled || !latest) return;
      if (latest !== currentBuildTime) {
        promptedRef.current = true;
        showUpdateToast();
      }
    };

    const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (!document.hidden) check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    // Run one check shortly after mount too.
    check();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  return null;
}
